import React, { useState, useEffect } from 'react';
import { 
  detectCourierCompany, 
  validateTrackingNumber, 
  getAvailableCourierCompanies,
  getValidationDescription,
  getTrackingUrl,
  COURIER_COMPANIES
} from '../../utils/trackingNumberUtils';

/**
 * Component สำหรับกรอกเลขพัสดุพร้อม auto-detect และ validation
 */
const TrackingNumberInput = ({ 
  value = '', 
  courierCompany: initialCourierCompany = null,
  customCourierCompany: initialCustomCourierCompany = null,
  onChange, 
  onSave,
  disabled = false,
  showTrackingLink = true
}) => {
  const [trackingNumber, setTrackingNumber] = useState(value);
  const [courierCompany, setCourierCompany] = useState(initialCourierCompany);
  const [customCourierCompany, setCustomCourierCompany] = useState(initialCustomCourierCompany || '');
  const [useCustomCompany, setUseCustomCompany] = useState(initialCustomCourierCompany ? true : false);
  const [detectedCompany, setDetectedCompany] = useState(null);

  useEffect(() => {
    setTrackingNumber(value);
  }, [value]);

  useEffect(() => {
    setCourierCompany(initialCourierCompany);
  }, [initialCourierCompany]);

  useEffect(() => {
    if (initialCustomCourierCompany) {
      setCustomCourierCompany(initialCustomCourierCompany);
      setUseCustomCompany(true);
    }
  }, [initialCustomCourierCompany]);

  // Auto-detect when tracking number changes (only suggest, don't auto-select)
  useEffect(() => {
    if (trackingNumber && trackingNumber.trim().length > 0) {
      const detected = detectCourierCompany(trackingNumber);
      setDetectedCompany(detected);
    } else {
      setDetectedCompany(null);
    }
  }, [trackingNumber]);

  const handleTrackingNumberChange = (e) => {
    const newValue = e.target.value;
    setTrackingNumber(newValue);
    if (onChange) {
      onChange(newValue, useCustomCompany ? null : courierCompany, useCustomCompany ? customCourierCompany : null);
    }
  };

  const handleCourierCompanyChange = (e) => {
    const newCompany = e.target.value;
    if (newCompany === 'custom') {
      setUseCustomCompany(true);
      setCourierCompany(null);
      if (onChange) {
        onChange(trackingNumber, null, customCourierCompany);
      }
    } else {
      setUseCustomCompany(false);
      setCourierCompany(newCompany);
      setCustomCourierCompany('');
      if (onChange) {
        onChange(trackingNumber, newCompany, null);
      }
    }
  };

  const handleCustomCourierCompanyChange = (e) => {
    const newCustomCompany = e.target.value;
    setCustomCourierCompany(newCustomCompany);
    if (onChange) {
      onChange(trackingNumber, null, newCustomCompany);
    }
  };

  const handleSave = () => {
    if (!trackingNumber.trim()) {
      alert('กรุณากรอกเลขพัสดุ');
      return;
    }

    const finalCourierCompany = useCustomCompany ? customCourierCompany.trim() : courierCompany;
    if (!finalCourierCompany) {
      alert('กรุณาเลือกหรือกรอกบริษัทขนส่ง');
      return;
    }

    if (onSave) {
      onSave(trackingNumber.trim(), useCustomCompany ? null : courierCompany, useCustomCompany ? customCourierCompany.trim() : null);
    }
  };

  const finalCourierCompany = useCustomCompany ? customCourierCompany : courierCompany;
  const trackingUrl = finalCourierCompany && trackingNumber && !useCustomCompany
    ? getTrackingUrl(trackingNumber, finalCourierCompany) 
    : null;

  const availableCompanies = getAvailableCourierCompanies();

  return (
    <div className="space-y-3">
      {/* Tracking Number Input */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          เลขพัสดุ *
        </label>
        <input
          type="text"
          value={trackingNumber}
          onChange={handleTrackingNumberChange}
          placeholder="กรุณาใส่เลขพัสดุ (เช่น JTE1234567890)"
          disabled={disabled}
          className={`
            w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors
            ${detectedCompany && !courierCompany && !useCustomCompany
              ? 'border-yellow-300 focus:ring-yellow-500'
              : 'border-gray-300 focus:ring-green-500'
            }
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          `}
        />
        
        {/* Auto-detect indicator (suggestion only) */}
        {detectedCompany && !useCustomCompany && (
          <p className="text-xs text-yellow-600 mt-1">
            💡 ตรวจพบ: {detectedCompany} {!courierCompany && '(คลิกเลือกบริษัทด้านล่าง)'}
          </p>
        )}
      </div>

      {/* Courier Company Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          บริษัทขนส่ง *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
          {availableCompanies.map(company => (
            <label
              key={company}
              className={`
                flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all
                ${courierCompany === company && !useCustomCompany
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
                ${detectedCompany === company && !courierCompany && !useCustomCompany
                  ? 'border-yellow-400 bg-yellow-50'
                  : ''
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input
                type="radio"
                name="courierCompany"
                value={company}
                checked={courierCompany === company && !useCustomCompany}
                onChange={handleCourierCompanyChange}
                disabled={disabled}
                className="w-4 h-4 text-green-600"
              />
              <span className="text-sm font-medium text-gray-700">{company}</span>
              {detectedCompany === company && !courierCompany && !useCustomCompany && (
                <span className="text-xs text-yellow-600">(ตรวจพบ)</span>
              )}
            </label>
          ))}
          
          {/* Custom Company Option */}
          <label
            className={`
              flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all
              ${useCustomCompany
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input
              type="radio"
              name="courierCompany"
              value="custom"
              checked={useCustomCompany}
              onChange={handleCourierCompanyChange}
              disabled={disabled}
              className="w-4 h-4 text-green-600"
            />
            <span className="text-sm font-medium text-gray-700">อื่นๆ</span>
          </label>
        </div>
        
        {/* Custom Company Input */}
        {useCustomCompany && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              กรอกชื่อบริษัทขนส่ง
            </label>
            <input
              type="text"
              value={customCourierCompany}
              onChange={handleCustomCourierCompanyChange}
              placeholder="เช่น บริษัทขนส่งของฉัน"
              disabled={disabled}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
            />
          </div>
        )}
      </div>

      {/* Tracking Link */}
      {showTrackingLink && trackingUrl && finalCourierCompany && trackingNumber && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            เปิดหน้า Tracking ของ {finalCourierCompany}
          </a>
        </div>
      )}

      {/* Save Button */}
      {onSave && (
        <button
          onClick={handleSave}
          disabled={disabled || !trackingNumber.trim() || (!courierCompany && !customCourierCompany.trim())}
          className={`
            w-full py-2 px-4 rounded-lg font-semibold transition-colors
            ${disabled || !trackingNumber.trim() || (!courierCompany && !customCourierCompany.trim())
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-purple-500 hover:bg-purple-600 text-white'
            }
          `}
        >
          บันทึกเลขพัสดุ
        </button>
      )}
    </div>
  );
};

export default TrackingNumberInput;

